import DstButton from "../../../../components/DstButton/DstButton";
import translate from "../../../../services/Translate";
import { JobBlockTag } from "../../LandingPageJobBlocks.d";
import { intToHex } from "../../LandingPageJobBlocks.function";
import { JobBlockTagsEditorProps, TagEditorRowProps } from "./JobBlockTagsEditor.d";

const DEFAULT_BACKGROUND_COLOR = 3615601;
const DEFAULT_TEXT_COLOR = 16777215;
const TAG_TEXT_MAX_LENGTH = 40;

const PREDEFINED_TAG_LIST: JobBlockTag[] = [
  {
    text: "Débutant",
    text_color: 0x627020,
    background_color: 0xd1f432,
  },
  {
    text: "Intermédiaire",
    text_color: 0x53539c,
    background_color: 0xdedefc,
  },
  {
    text: "Confirmé",
    text_color: 0xe8e8fd,
    background_color: 0x373771,
  },
];

const _hexColorToInteger = (hexColor: string) => parseInt(hexColor.replace("#", ""), 16);

const TagEditorRow = ({ tagData, tagPosition, onTagUpdate, onTagRemove, language }: TagEditorRowProps) => {
  return (
    <div className="border rounded px-3 py-2 mb-2">
      <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-2 gap-lg-3">
        <span
          className="px-3 py-1 rounded s-regular fw-semibold text-center d-inline-block text-truncate"
          style={{
            backgroundColor: intToHex(tagData.background_color ?? DEFAULT_BACKGROUND_COLOR),
            color: intToHex(tagData.text_color ?? DEFAULT_TEXT_COLOR),
            width: 200,
          }}
          title={tagData.text}
        >
          {tagData.text || translate(language, "PAGES.LANDING_PAGE_JOB_BLOCKS.TAGS_EDITOR.PREVIEW")}
        </span>

        <input
          className="form-control form-control-sm w-100 w-lg-auto"
          placeholder={translate(language, "PAGES.LANDING_PAGE_JOB_BLOCKS.TAGS_EDITOR.FIELDS.TEXT_PLACEHOLDER")}
          maxLength={TAG_TEXT_MAX_LENGTH}
          value={tagData.text}
          onChange={(event) => onTagUpdate(tagPosition, "text", event.target.value)}
        />

        <div className="d-flex gap-2">
          <input
            type="color"
            className="form-control form-control-color form-control-sm"
            value={intToHex(tagData.background_color ?? DEFAULT_BACKGROUND_COLOR)}
            onChange={(event) => onTagUpdate(tagPosition, "background_color", _hexColorToInteger(event.target.value))}
          />

          <input
            type="color"
            className="form-control form-control-color form-control-sm"
            value={intToHex(tagData.text_color ?? DEFAULT_TEXT_COLOR)}
            onChange={(event) => onTagUpdate(tagPosition, "text_color", _hexColorToInteger(event.target.value))}
          />
          <DstButton
            value="✕"
            variant="outline-danger"
            btnClass="align-self-start align-self-lg-center"
            clickFunction={() => onTagRemove(tagPosition)}
          />
        </div>
      </div>
    </div>
  );
};

const JobBlockTagsEditor = ({ value: tagList, onChange: onTagListChange, language }: JobBlockTagsEditorProps) => {
  const updateTagField = (tagPosition: number, fieldName: keyof JobBlockTag, fieldValue: any) => {
    const updatedTagList = [...tagList];
    updatedTagList[tagPosition] = {
      ...updatedTagList[tagPosition],
      [fieldName]: fieldValue,
    };
    onTagListChange(updatedTagList);
  };

  const addEmptyTag = () => {
    if (tagList.some((tagItem) => !tagItem.text?.trim())) return;

    onTagListChange([
      ...tagList,
      {
        text: "",
        background_color: DEFAULT_BACKGROUND_COLOR,
        text_color: DEFAULT_TEXT_COLOR,
      },
    ]);
  };

  const addPredefinedTag = (predefinedTag: JobBlockTag) => {
    if (tagList.some((existingTag) => existingTag.text.toLowerCase() === predefinedTag.text.toLowerCase())) {
      return;
    }

    onTagListChange([...tagList, { ...predefinedTag }]);
  };

  return (
    <div>
      <div className="mb-2 fw-semibold">{translate(language, "PAGES.LANDING_PAGE_JOB_BLOCKS.TAGS_EDITOR.TITLE")}</div>

      <div className="d-flex gap-2 flex-wrap mb-3">
        {PREDEFINED_TAG_LIST.map((predefinedTag) => (
          <DstButton
            key={predefinedTag.text}
            value={predefinedTag.text}
            variant="light"
            disabled={tagList.some(
              (existingTag) => existingTag.text.toLowerCase() === predefinedTag.text.toLowerCase()
            )}
            clickFunction={() => addPredefinedTag(predefinedTag)}
          />
        ))}
      </div>

      {tagList.map((tagItem, tagPosition) => (
        <TagEditorRow
          key={tagPosition}
          tagData={tagItem}
          tagPosition={tagPosition}
          onTagUpdate={updateTagField}
          onTagRemove={() => onTagListChange(tagList.filter((_, position) => position !== tagPosition))}
          language={language}
        />
      ))}

      <DstButton
        btnClass="mt-2"
        value={translate(language, "PAGES.LANDING_PAGE_JOB_BLOCKS.TAGS_EDITOR.BUTTON.ADD_TAG")}
        variant="outline-dark"
        clickFunction={addEmptyTag}
        disabled={tagList.some((tagItem) => !tagItem.text?.trim())}
      />
    </div>
  );
};

export default JobBlockTagsEditor;
